// PRD-0191 — brand visual kit, content visual slots and handoff images. Shared by the backend,
// frontend and the hc SDK/CLI/MCP.
//
// One asset table, many sources: every image is a `media_assets` row with provenance and rights.
// Content never embeds files; it points at assets through slots (hero, og, ...). A slot can hold
// several proposals and at most one selected asset.

/** P1 slots. P2 adds social_square, social_portrait, pin and inline:<n>. */
export const VISUAL_SLOTS = ["hero", "og"] as const;
export type VisualSlot = typeof VISUAL_SLOTS[number];

export const VISUAL_SLOT_STATUSES = ["proposed", "selected", "rejected"] as const;
export type VisualSlotStatus = typeof VISUAL_SLOT_STATUSES[number];

/** Where a proposal came from. `reuse` = an existing library asset. */
export const VISUAL_PROPOSAL_SOURCES = ["reuse", "generated", "rendered", "manual"] as const;
export type VisualProposalSource = typeof VISUAL_PROPOSAL_SOURCES[number];

/** Only these rights may fill a publishable slot (selection, approval and handoff export). */
export const PUBLISHABLE_USAGE_RIGHTS = ["owned", "licensed", "ugc_approved"] as const;
export function isPublishableRights(value: string | null | undefined): boolean {
  return (PUBLISHABLE_USAGE_RIGHTS as readonly string[]).includes(String(value ?? ""));
}

/** Spec names for renditions; P1 produces the first two. */
export const RENDITIONS = { hero: "hero_16x9", og: "og_1200x630" } as const;
export const OG_WIDTH = 1200;
export const OG_HEIGHT = 630;

// ── Brand visual kit ─────────────────────────────────────────────────────────

export const PALETTE_ROLES = ["background", "surface", "text", "muted", "accent", "highlight"] as const;
export type PaletteRole = typeof PALETTE_ROLES[number];

export const FONT_ROLES = ["heading", "body", "mono"] as const;
export type FontRole = typeof FONT_ROLES[number];

export interface VisualKitFont {
  role: FontRole;
  family: string;
  weight: number;
  style: "normal" | "italic";
  /** Mech Storage object holding the TTF/OTF/WOFF bytes (woff2 is converted to TTF on upload). */
  storage_id: string;
  storage_key: string;
  content_hash: string;
  format: "ttf" | "otf" | "woff";
  file_size_bytes: number;
  license: string | null;
}

export interface VisualKitLogos {
  primary: string | null;
  mark: string | null;
  light: string | null;
  dark: string | null;
}

export interface VisualKitImageStyle {
  /** Short art-direction block passed to the image model. */
  art_direction: string;
  do: string[];
  dont: string[];
  /** 0–6 owned brand assets passed to mech-media as `source_images`. */
  reference_asset_ids: string[];
}

export interface VisualKitTemplates {
  /** Enabled card layouts. P1: `og_basic`. */
  enabled: string[];
  /** Small uppercase label above the title on cards, e.g. `Blog`. */
  eyebrow: string | null;
  /** Wordmark text shown on cards when there is no logo. */
  wordmark: string | null;
}

/** The editable part of a kit (PUT body). */
export interface VisualKitSpec {
  /** Hex colors (#RRGGBB) by role. Contrast pairs are computed on save. */
  palette: Record<PaletteRole, string>;
  /** Declared font families by role (from tokens). Each needs an uploaded file in `fonts` to render. */
  typography: Partial<Record<FontRole, string>>;
  fonts: VisualKitFont[];
  logos: VisualKitLogos;
  image_style: VisualKitImageStyle;
  templates: VisualKitTemplates;
  /** Where the tokens came from, e.g. `mech-browse/design/design-system.json`. */
  source: string | null;
}

export interface ContrastPair {
  foreground: PaletteRole;
  background: PaletteRole;
  ratio: number;
  /** WCAG AA for normal text (>= 4.5). */
  passes_aa: boolean;
}

/**
 * A kit font file the web app can load (content preview). `path` is an API path
 * (`/api/v1/brands/:id/visual-kit/fonts/:role?style=..&v=<kit version>`) that streams the bytes with
 * CORS + credentials: fonts load in CORS mode and the shared Mech Storage bucket has no CORS for the
 * app, so signed storage URLs cannot be used for fonts. No storage key or object id is included.
 */
export interface VisualKitFontFile {
  role: FontRole;
  family: string;
  weight: number;
  style: "normal" | "italic";
  format: VisualKitFont["format"];
  path: string;
}

export interface BrandVisualKit extends VisualKitSpec {
  id: string;
  brand_id: string;
  /** Monotonic per brand; the highest version is current. */
  version: number;
  contrast: ContrastPair[];
  created_by: string;
  created_at: string;
  /**
   * `GET /visual-kit` only: where the web app fetches each font in `fonts` (render with fallbacks
   * until loaded). Absent on write responses.
   */
  font_files?: VisualKitFontFile[];
}

export interface VisualKitPutInput {
  palette?: Partial<Record<PaletteRole, string>>;
  typography?: Partial<Record<FontRole, string | null>>;
  logos?: Partial<VisualKitLogos>;
  image_style?: Partial<VisualKitImageStyle>;
  templates?: Partial<VisualKitTemplates>;
  source?: string | null;
  /** Optimistic concurrency: the version you edited. Omit to overwrite. */
  expected_version?: number;
}

/** `POST /visual-kit/import`: a design-tokens JSON (ThinkRun mech-browse format or a flat token map). */
export interface VisualKitImportInput {
  tokens: Record<string, unknown>;
  source?: string;
  expected_version?: number;
}

/** `GET /visual-kit/preview`: the rendered card stored in Mech Storage, as a signed URL. */
export interface VisualKitPreview {
  url: string;
  expires_at: string;
  width: number;
  height: number;
}

/** `POST /visual-kit/fonts`: one font file, base64. woff2 is converted to TTF. */
export interface VisualKitFontUploadInput {
  role: FontRole;
  family: string;
  weight?: number;
  style?: "normal" | "italic";
  filename: string;
  data_base64: string;
  license?: string;
  expected_version?: number;
}

// ── Slots ────────────────────────────────────────────────────────────────────

export interface VisualAssetSummary {
  id: string;
  /** Signed, short-lived Mech Storage URL (about 1h) the browser loads directly. */
  url: string | null;
  url_expires_at: string | null;
  /** Deprecated alias of `url`. */
  preview_url: string | null;
  mime_type: string;
  width: number | null;
  height: number | null;
  alt_text: string | null;
  primary_source: string;
  usage_rights: string;
  rendition: string | null;
  parent_asset_id: string | null;
  publishable: boolean;
}

export interface VisualSlotEntry {
  id: string;
  slot: VisualSlot;
  status: VisualSlotStatus;
  source: VisualProposalSource;
  asset_id: string;
  asset: VisualAssetSummary | null;
  chosen_by: string | null;
  note: string | null;
  /** OG card rendered from a hero other than the selected one; re-render before publishing. */
  stale?: boolean;
  created_at: string;
  updated_at: string;
}

export interface BriefVisuals {
  brief_id: string;
  brand_id: string;
  slots: Record<VisualSlot, { selected: VisualSlotEntry | null; proposals: VisualSlotEntry[] }>;
  /** sha256 of the selected slot→asset pairs; the approval and Ready pin this. */
  selection_hash: string;
  /** Whether the brand has a visual kit (generation needs one). */
  has_kit: boolean;
  /** Partial outcomes of the last change, e.g. the OG card could not be re-rendered after a hero change. */
  warnings?: string[];
}

export interface VisualsGenerateInput {
  slots?: VisualSlot[];
  /** Extra direction for the image model, e.g. "warmer, show a terminal". */
  note?: string;
  /** Skip reuse-first and always generate new hero options. */
  regenerate?: boolean;
}

export interface VisualsGenerateAccepted {
  run_id: string;
  brief_id: string;
  slots: VisualSlot[];
}

export interface VisualSlotSelectInput {
  asset_id: string;
}

// ── Handoff images (PRD-0190 export `images[]`) ──────────────────────────────

export interface PublishImageExport {
  slot: VisualSlot;
  asset_id: string;
  /** Signed absolute URL, valid 7 days, re-issued on every export. */
  url: string;
  alt: string;
  width: number | null;
  height: number | null;
  mime: string;
  filename: string;
  /** Where the site repo should put it, e.g. `frontend/public/og/blog-<slug>.png`. */
  suggested_path: string;
}
